/**
 * FELVORA — ago-2026 · PANEL DE CAMPOS DEL BIEN POR ACTIVIDAD (super_admin).
 *
 * Edita `av_actividad_config.bien_campos` de cualquiera de las 17 actividades del Art. 17.
 * El cambio surte efecto de inmediato: el formulario del bien de esa actividad se rearma
 * en la siguiente carga, sin desplegar nada. Cada guardado deja bitácora en el backend
 * (`PATCH /api/plataforma/av/actividades/{giro}` → log_audit).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowUp, ArrowDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const TIPOS = ["texto", "textarea", "numero", "moneda", "fecha", "opcion", "cliente",
               "asesor"];

export default function PanelCamposBien() {
  const [actividades, setActividades] = useState([]);
  const [giro, setGiro] = useState("");
  const [campos, setCampos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const r = await api.get("/plataforma/av/actividades");
      setActividades(r.data || []);
      if (!giro && (r.data || []).length) setGiro(r.data[0].giro);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const actual = actividades.find((a) => a.giro === giro);
  useEffect(() => { setCampos(actual?.bien_campos || []); }, [giro, actividades]); // eslint-disable-line

  const editar = (i, k, v) => setCampos(campos.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const mover = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= campos.length) return;
    const copia = [...campos];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    setCampos(copia);
  };
  const agregar = () => setCampos([...campos, {
    clave: "", label: "", tipo: "texto", opciones: [], requerido: false,
    requerido_aviso: false, ayuda: "", elemento: "", fuente: "interno", ancho: "",
  }]);
  const quitar = (i) => setCampos(campos.filter((_, idx) => idx !== i));

  const guardar = async () => {
    if (campos.some((c) => !c.clave || !c.label)) {
      toast.error("Cada campo necesita clave interna y etiqueta visible.");
      return;
    }
    setGuardando(true);
    try {
      await api.patch(`/plataforma/av/actividades/${giro}`, { bien_campos: campos });
      toast.success(`Campos de ${giro} guardados. El formulario ya cambió.`);
      await cargar();
    } catch (e) { toast.error(apiError(e)); }
    setGuardando(false);
  };

  return (
    <Card data-testid="panel-campos-bien">
      <CardHeader>
        <CardTitle className="text-base">Campos del bien por actividad vulnerable</CardTitle>
        <p className="text-sm text-slate-500">
          El formulario de alta del bien de cada actividad se arma con esta lista. Los campos
          marcados como obligatorios para el aviso bloquean la generación del XML cuando
          están vacíos (el bien sí se guarda) y salen en el semáforo de faltantes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs">Actividad</label>
            <Select value={giro} onValueChange={setGiro}>
              <SelectTrigger className="w-72" data-testid="campos-select-giro">
                <SelectValue placeholder="Elige la actividad" />
              </SelectTrigger>
              <SelectContent>
                {actividades.map((a) => (
                  <SelectItem key={a.giro} value={a.giro}>
                    {a.bien_label} · {a.giro}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={agregar} data-testid="campos-agregar">
            <Plus className="mr-1 h-4 w-4" /> Agregar campo
          </Button>
          <Button onClick={guardar} disabled={guardando} data-testid="campos-guardar">
            <Save className="mr-1 h-4 w-4" /> {guardando ? "Guardando…" : "Guardar cambios"}
          </Button>
          {actual?.bien_campos_editado_por && (
            <Badge variant="outline" data-testid="campos-editado-por">
              Editado por {actual.bien_campos_editado_por}
            </Badge>
          )}
        </div>

        <div className="space-y-2">
          {campos.map((c, i) => (
            <div key={i} data-testid={`campos-fila-${i}`}
                 className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 md:grid-cols-12">
              <div className="md:col-span-2">
                <label className="text-xs">Clave interna</label>
                <Input value={c.clave} data-testid={`campos-clave-${i}`}
                       onChange={(e) => editar(i, "clave", e.target.value)} />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs">Etiqueta visible</label>
                <Input value={c.label} data-testid={`campos-label-${i}`}
                       onChange={(e) => editar(i, "label", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs">Tipo</label>
                <Select value={c.tipo} onValueChange={(v) => editar(i, "tipo", v)}>
                  <SelectTrigger data-testid={`campos-tipo-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs">Opciones (separadas por coma)</label>
                <Input value={(c.opciones || []).join(", ")}
                       data-testid={`campos-opciones-${i}`}
                       onChange={(e) => editar(i, "opciones",
                         e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />
              </div>
              <div className="flex items-end justify-end gap-1 md:col-span-2">
                <Button size="icon" variant="ghost" onClick={() => mover(i, -1)}
                        data-testid={`campos-subir-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => mover(i, 1)}
                        data-testid={`campos-bajar-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => quitar(i)}
                        data-testid={`campos-quitar-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="md:col-span-5">
                <label className="text-xs">Texto de ayuda</label>
                <Input value={c.ayuda || ""} data-testid={`campos-ayuda-${i}`}
                       onChange={(e) => editar(i, "ayuda", e.target.value)} />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs">Elemento del XSD</label>
                <Input value={c.elemento || ""} data-testid={`campos-elemento-${i}`}
                       onChange={(e) => editar(i, "elemento", e.target.value)} />
              </div>
              <div className="flex items-end gap-4 md:col-span-4">
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox checked={!!c.requerido} data-testid={`campos-req-${i}`}
                            onCheckedChange={(v) => editar(i, "requerido", !!v)} />
                  Obligatorio al capturar
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox checked={!!c.requerido_aviso} data-testid={`campos-reqaviso-${i}`}
                            onCheckedChange={(v) => editar(i, "requerido_aviso", !!v)} />
                  Obligatorio para el aviso
                </label>
                <Badge variant="outline" className="text-xs">{c.fuente || "interno"}</Badge>
              </div>
            </div>
          ))}
          {campos.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">
              Esta actividad no tiene campos declarados: su formulario del bien aparecería vacío.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
